'use client';

/**
 * Inline freshness badge — "data through 2026-05-02" with green/amber/red coloring
 * based on staleness. Drop into any tab/page header so users can see how current
 * the underlying data is at the point of consumption (not just on /pipeline).
 *
 * Usage:
 *   <DataAsOf surfaceId="overview" />
 *
 * Surface ids come from lib/data-surfaces.ts. The /api/data-surfaces fetch is
 * shared across all DataAsOf instances via useResource — every badge on the
 * page reads from the same cached payload.
 */

import { useResource } from '@/lib/use-resource';

interface SurfaceStatus {
  id: string;
  freshestAsOf: string | null;
  freshestMtime: string | null;
  staleDays: number | null;
  status: 'ok' | 'stale' | 'empty' | 'missing';
}

interface SurfacesResponse {
  today: string;
  surfaces: SurfaceStatus[];
}

export function DataAsOf({ surfaceId, className = '' }: { surfaceId: string; className?: string }) {
  const { data } = useResource<SurfacesResponse>('/api/data-surfaces');
  const s = data?.surfaces.find((x) => x.id === surfaceId) ?? null;

  if (!s || !s.freshestAsOf) {
    return null;
  }

  const asOfDay = s.freshestAsOf.slice(0, 10);
  const stale = s.status === 'stale' || (s.staleDays != null && s.staleDays > 0);
  const broken = s.status === 'empty' || s.status === 'missing';

  // When the underlying file was last written today, append HH:MM so users
  // can see at a glance how recently the data refreshed (e.g. after clicking
  // "Update Portfolio"). Stale data hides the time — there's no point.
  let timeLabel = '';
  if (!stale && s.freshestMtime) {
    const m = new Date(s.freshestMtime);
    if (!Number.isNaN(m.getTime())) {
      timeLabel = ` ${String(m.getHours()).padStart(2, '0')}:${String(m.getMinutes()).padStart(2, '0')}`;
    }
  }

  const colorClass = broken
    ? 'text-red-300 border-red-400/30 bg-red-400/5'
    : stale
      ? 'text-amber-300 border-amber-400/30 bg-amber-400/5'
      : 'text-emerald-300 border-emerald-400/20 bg-emerald-400/5';

  const ageLabel = s.staleDays === 0
    ? 'today'
    : s.staleDays === 1
      ? '1 day ago'
      : s.staleDays != null
        ? `${s.staleDays}d ago`
        : '';

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-mono tabular-nums px-2 py-0.5 rounded border ${colorClass} ${className}`}
      title={`Data through ${asOfDay} · ${ageLabel}`}
    >
      <span className={`w-1 h-1 rounded-full ${broken ? 'bg-red-400' : stale ? 'bg-amber-300' : 'bg-emerald-400'}`} />
      data through {asOfDay}{timeLabel}
    </span>
  );
}
